/**
 * Shared types for CoverVision frontend.
 *
 * These types are used across Search.tsx, ResultsSidebar.tsx, and other components.
 */

/**
 * Represents a single book result from the search API.
 */
export interface BookResult {
  id: number;
  isbn: string;
  title: string;
  author: string;
  binding_type: string;
  sales_rank: number;
  cover_url: string;
  distance: number;
  confidence: number;
  /** Indicates the AI found no reliable match */
  no_reliable_match?: boolean;
  /** Indicates this is a pending result from SKU workflow */
  is_pending?: boolean;
  /** LLM verification details if applied */
  llm_verification?: {
    applied: boolean;
    confidence: number;
    reasoning: string;
  };
  /** Indicates reranker was used for this result */
  reranker_used?: boolean;
  /** Indicates AI verification was skipped */
  ai_verification_skipped?: boolean;
  /** CLIP score if CLIP reranker was used */
  clip_score?: number;
  /** Combined score from reranker */
  combined_score?: number;
}

/**
 * Represents a scan result, including match status and metadata.
 */
export interface ScanResult {
  /** Unique ID for this scan request */
  id: number;
  /** Match status from the API */
  match_status?: 'found' | 'not_found' | 'error' | 'pending';
  /** ASIN, ISBN, ZB_ID, or SKU identifier */
  identifier?: string;
  /** Book title or LLM-generated description */
  description?: string;
  /** Cover image URL from backend */
  cover_url?: string;
  /** Search results array */
  results: BookResult[];
  /** Binding type used for this scan */
  bindingType: string;
  /** Timestamp when scan was submitted */
  timestamp: number;
}

/**
 * Represents an item in the processing queue.
 */
export interface ProcessingQueueItem {
  id: number;
  imageBlob: Blob;
  timestamp: number;
  bindingType: string;
  status: 'queued' | 'processing' | 'completed' | 'error';
  retryCount: number;
}

/**
 * Binding type options.
 */
export type BindingType = 'hardcover' | 'paperback' | 'barcode';

/**
 * API configuration.
 */
export const API_CONFIG = {
  /** Production API URL */
  PRODUCTION_URL: 'https://api.covervisionai.com/api/v1',
  /** Development API URL */
  DEVELOPMENT_URL: 'http://localhost:8000/api/v1',
  /** Maximum queue size */
  MAX_QUEUE_SIZE: 10,
  /** API timeout in milliseconds */
  TIMEOUT_MS: 30000,
  /** Maximum history length */
  MAX_HISTORY_LENGTH: 20,
  /** Default top_k for search results */
  DEFAULT_TOP_K: 5,
} as const;

export const API_BASE_URL = API_CONFIG.PRODUCTION_URL;

/**
 * Session storage keys.
 */
export const STORAGE_KEYS = {
  SCAN_HISTORY: 'covervision_scan_history',
  SELECTED_PRINTER: 'selectedPrinter',
  LAST_PRINTED_SKU: 'lastPrintedSku',
} as const;
